import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid'

export type DeletableLegend = {
  id: number
  parameter: string
  cropId?: number | null
  cropYear?: number | null
}

/*
 * Resolves a DataGrid selection against the rows on screen.
 *
 * Two things make this more than a Set lookup. A select-all arrives as
 * { type: 'exclude' } listing the rows that are *not* selected, so counting
 * `ids` reads a full selection as an empty one. And a selection survives a
 * filter change, so it can still name rows the user can no longer see — those
 * are dropped rather than acted on.
 */
export const resolveSelectedRows = <T extends { id: GridRowId }>(
  rows: T[],
  selection?: GridRowSelectionModel | null,
): T[] => {
  const ids = selection?.ids ?? new Set<GridRowId>()
  return selection?.type === 'exclude'
    ? rows.filter((row) => !ids.has(row.id))
    : rows.filter((row) => ids.has(row.id))
}

/*
 * Deletes a selection one legend at a time, reporting which ones failed instead
 * of abandoning the batch at the first error.
 *
 * Sequential rather than parallel: every delete fans out through the legend
 * reducers to recolour the loaded layers, and a failure has to stay attributable
 * to one legend so the dialog can say what is left to retry.
 */
export const deleteLegendsInOrder = async <T extends { id: number }>(
  legends: T[],
  deleteOne: (legend: T) => Promise<unknown>,
): Promise<{ deletedIds: number[]; failures: T[] }> => {
  const deletedIds: number[] = []
  const failures: T[] = []
  for (const legend of legends) {
    try {
      await deleteOne(legend)
      deletedIds.push(legend.id)
    } catch {
      failures.push(legend)
    }
  }
  return { deletedIds, failures }
}

// The tenant-global DEM legend, which the crop-legend page shows outside the
// grid and has to refresh separately after a delete.
export const isGlobalDemLegend = (legend: DeletableLegend) =>
  legend?.parameter === 'dem' && !legend?.cropId
